import { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

interface RatingStarsProps {
  rating: number;
  maxRating?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showValue?: boolean;
  showCount?: boolean;
  count?: number;
  className?: string;
}

const sizeClasses = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

const textSizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

export function RatingStars({
  rating,
  maxRating = 5,
  size = 'sm',
  showValue = false,
  showCount = false,
  count = 0,
  className,
}: RatingStarsProps) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <div className="flex items-center">
        {/* Full stars */}
        {Array.from({ length: fullStars }).map((_, i) => (
          <StarIcon
            key={`full-${i}`}
            className={cn(sizeClasses[size], 'text-accent-400')}
          />
        ))}

        {/* Half star */}
        {hasHalfStar && (
          <div className="relative">
            <StarOutlineIcon
              className={cn(sizeClasses[size], 'text-accent-400')}
            />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <StarIcon
                className={cn(sizeClasses[size], 'text-accent-400')}
              />
            </div>
          </div>
        )}

        {/* Empty stars */}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <StarOutlineIcon
            key={`empty-${i}`}
            className={cn(sizeClasses[size], 'text-gray-300 dark:text-dark-600')}
          />
        ))}
      </div>

      {showValue && (
        <span className={cn(textSizeClasses[size], 'font-medium text-gray-700 dark:text-gray-300 ml-1')}>
          {rating.toFixed(1)}
        </span>
      )}

      {showCount && count > 0 && (
        <span className={cn(textSizeClasses[size], 'text-gray-500 dark:text-gray-400')}>
          ({count.toLocaleString()})
        </span>
      )}
    </div>
  );
}

interface RatingInputProps {
  value: number;
  onChange: (rating: number) => void;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

export function RatingInput({
  value,
  onChange,
  maxRating = 5,
  size = 'md',
  disabled = false,
  className,
}: RatingInputProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const displayValue = hoverValue !== null ? hoverValue : value;

  return (
    <div
      className={cn(
        'flex items-center gap-0.5',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onMouseLeave={() => setHoverValue(null)}
    >
      {Array.from({ length: maxRating }).map((_, i) => {
        const starValue = i + 1;
        const isFilled = starValue <= displayValue;

        return (
          <button
            key={i}
            type="button"
            disabled={disabled}
            className={cn(
              'transition-transform duration-150',
              !disabled && 'hover:scale-110 cursor-pointer',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded'
            )}
            onClick={() => onChange(starValue)}
            onMouseEnter={() => !disabled && setHoverValue(starValue)}
          >
            {isFilled ? (
              <StarIcon
                className={cn(
                  sizeClasses[size],
                  'text-accent-400 transition-colors'
                )}
              />
            ) : (
              <StarOutlineIcon
                className={cn(
                  sizeClasses[size],
                  'text-gray-300 dark:text-dark-600 transition-colors',
                  !disabled && 'hover:text-accent-300'
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

interface RatingBreakdownProps {
  breakdown: { stars: number; count: number; percentage: number }[];
  totalReviews: number;
  averageRating: number;
  className?: string;
}

export function RatingBreakdown({
  breakdown,
  totalReviews,
  averageRating,
  className,
}: RatingBreakdownProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary */}
      <div className="flex items-center gap-4">
        <div className="text-center">
          <div className="text-4xl font-bold text-gray-900 dark:text-white">
            {averageRating.toFixed(1)}
          </div>
          <RatingStars rating={averageRating} size="sm" className="justify-center" />
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {totalReviews.toLocaleString()} reviews
          </div>
        </div>

        {/* Breakdown bars */}
        <div className="flex-1 space-y-2">
          {breakdown.map(({ stars, count, percentage }) => (
            <div key={stars} className="flex items-center gap-2 text-sm">
              <span className="w-12 text-gray-600 dark:text-gray-400">
                {stars} star
              </span>
              <div className="flex-1 h-2 bg-gray-200 dark:bg-dark-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent-400 rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="w-12 text-right text-gray-500 dark:text-gray-400">
                {percentage}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
